import type {
	CreateWishlistModel,
	WishlistModel,
	WishlistModelTransformed,
	WishlistPropertyResponse
} from '~/core/entities/api/ApiWishlist';
import type { ResultTuple } from '~~/core/helpers/result';
import type { AddPropertyToWishlistParam } from '~~/core/ports/repositories/WishlistRepository';

import { Result } from '~~/core/helpers/result';
import { createOriginalErrorObject } from '~~/server/utils/errorHandling';

interface NuxtWishlistRepository {
	listMy(): Promise<ResultTuple<WishlistModelTransformed[]>>;
	getById(wishlistId: string): Promise<ResultTuple<WishlistModelTransformed>>;
	create(payload: CreateWishlistModel): Promise<ResultTuple<WishlistModel>>;
	delete(wishlistId: string): Promise<ResultTuple<{ ok: true }>>;
	addProperty(
		params: AddPropertyToWishlistParam
	): Promise<ResultTuple<WishlistPropertyResponse>>;
	removeProperty(params: {
		propertyId: string;
		wishlistId?: string;
	}): Promise<ResultTuple<WishlistPropertyResponse>>;
}

export const nuxtWishlistRepository: NuxtWishlistRepository = {
	async listMy() {
		try {
			const response = await $fetch<WishlistModelTransformed[]>(
				`/api/wishlist`,
				{
					method: 'GET'
				}
			);
			return Result.ok(response);
		} catch (error) {
			return Result.error({
				message: 'Error while fetching wishlists from the API.',
				source: 'nuxtWishlistRepository.listMy',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	},
	async getById(wishlistId) {
		try {
			const response = await $fetch<WishlistModelTransformed>(
				`/api/wishlist/${wishlistId}`,
				{
					method: 'GET'
				}
			);
			return Result.ok(response);
		} catch (error) {
			return Result.error({
				message: 'Error while fetching wishlist by ID from the API.',
				source: 'nuxtWishlistRepository.getById',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	},
	async create(payload) {
		try {
			const response = await $fetch<WishlistModel>(`/api/wishlist`, {
				method: 'POST',
				body: payload
			});
			return Result.ok(response);
		} catch (error) {
			return Result.error({
				message: 'Error while creating wishlist.',
				source: 'nuxtWishlistRepository.create',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	},
	async delete(wishlistId) {
		try {
			const response = await $fetch<{ ok: true }>(
				`/api/wishlist/${wishlistId}`,
				{
					method: 'DELETE'
				}
			);
			return Result.ok(response);
		} catch (error) {
			return Result.error({
				message: 'Error while deleting wishlist.',
				source: 'nuxtWishlistRepository.delete',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	},
	async addProperty(params) {
		try {
			const response = await $fetch<{ result: WishlistPropertyResponse }>(
				`/api/wishlist/add`,
				{
					method: 'POST',
					body: params
				}
			);
			return Result.ok(response.result);
		} catch (error) {
			return Result.error({
				message: 'Error while adding property to wishlist.',
				source: 'nuxtWishlistRepository.addProperty',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	},
	async removeProperty(params) {
		try {
			const response = await $fetch<{ result: WishlistPropertyResponse }>(
				`/api/wishlist/remove`,
				{
					method: 'POST',
					body: params
				}
			);
			return Result.ok(response.result);
		} catch (error) {
			return Result.error({
				message: 'Error while removing property from wishlist.',
				source: 'nuxtWishlistRepository.removeProperty',
				originalErrorObject: createOriginalErrorObject(error)
			});
		}
	}
};
